import React from 'react';
import { useHistory } from 'react-router-dom';
import {
    IconButton, Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem, Divider,
} from '@material-ui/core';
import { AccountCircle } from '@material-ui/icons';

import { useAuth } from '../../context/auth';
import { useColorMode } from '../../context/colorMode/colorMode.context';
import { clearAllBrandingCaches } from '../../context/branding/branding.context';
import { logoutUser, switchWorkspaceService } from '../../services/auth.service';
import { LOCAL_STORAGE_KEYS } from '../../utils/constants';
import OrgPickerDialog from '../OrgPickerDialog/OrgPickerDialog.comp';

/**
 * The account control: the avatar button, its dropdown, and the workspace picker.
 *
 * Lifted out of Main.comp.jsx unchanged so the pages that sit OUTSIDE MainLayout
 * — the LearnCreator front door, and anything else on MinimalLayout — show the
 * same menu instead of a second copy of it. One definition means "Settings" and
 * "Sign out" cannot drift apart between layouts, which is exactly what started
 * to happen.
 *
 * Every item, its wording and its destination are as they were in the layout:
 * the account email as a disabled header, Settings, Switch Workspace when the
 * person belongs to more than one, and Sign out.
 *
 * @param {string}   [className]  class for the avatar button (MainLayout passes
 *                                its fixed-position one)
 * @param {object}   [style]      inline style for the avatar button
 * @param {string}   [buttonId]   DOM id for the button
 * @param {Function} [onLogout]   overrides the sign-out action. MainLayout hands
 *                                in its own, which clears a couple of extra keys
 *                                and is also wired to the sidebar's Log out — so
 *                                that behaviour stays in one place there rather
 *                                than being half-moved here.
 */
const AccountMenu = ({ className, style, buttonId = 'user-profile-menu-button', onLogout }) => {
    const history = useHistory();
    const { authUser, setAuthToken, setAuthUser } = useAuth();
    const { isDark: isDarkMode } = useColorMode();

    const anchorRef = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const [wsPickerOpen, setWsPickerOpen] = React.useState(false);
    const [wsSwitching, setWsSwitching] = React.useState(false);

    const handleToggle = () => setOpen((prev) => !prev);

    const handleClose = (event) => {
        // A click on the avatar itself is the toggle, not an outside click.
        if (anchorRef.current && event && anchorRef.current.contains(event.target)) return;
        setOpen(false);
    };

    const handleListKeyDown = (event) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        }
    };

    // Return focus to the button when the menu closes.
    const prevOpen = React.useRef(open);
    React.useEffect(() => {
        if (prevOpen.current === true && open === false && anchorRef.current) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const workspaceMemberships = authUser?.workspace_memberships || [];
    const isMultiWorkspace = workspaceMemberships.filter((m) => m?.is_active !== false).length > 1;

    /*
     * A user can hold two roles for the same workspace (peer + client), so
     * dq_insider_id alone does not identify the membership they are acting in.
     * Drives the "current" marker in the picker and the no-op guard below.
     */
    const activeMembership =
        workspaceMemberships.find(
            (m) => m.dq_insider_id === authUser?.dq_insider_id
                && (m.role === 'peer') === (authUser?.is_peer === true)
                && (m.org_code || null) === (authUser?.org_code || null),
        ) || workspaceMemberships.find((m) => m.dq_insider_id === authUser?.dq_insider_id);
    const currentRole = activeMembership?.role || (authUser?.is_peer ? 'peer' : null);

    const handleWorkspaceSelect = async (dq_insider_id, role) => {
        // Re-selecting the same workspace AND role is a no-op.
        if (dq_insider_id === authUser?.dq_insider_id && (!role || role === currentRole)) {
            setWsPickerOpen(false);
            return;
        }
        try {
            setWsSwitching(true);
            const result = await switchWorkspaceService(dq_insider_id, role);
            if (result.data && result.data.user) {
                await setAuthToken(result.data.auth_token);
                await setAuthUser(result.data.user);
                if (result.data.refresh_token) {
                    localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(result.data.refresh_token));
                }
                localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(result.data.user.email));
                setWsPickerOpen(false);
                clearAllBrandingCaches();
                // Full reload so every workspace-scoped view re-fetches under the
                // new session.
                window.location.assign('/my_workspace');
            }
        } catch (e) {
            // already surfaced by invokeApi
        } finally {
            setWsSwitching(false);
        }
    };

    const defaultLogout = async () => {
        try {
            await logoutUser();
            setAuthToken(null);
            setAuthUser(null);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.auth_token);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.auth_user);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.refresh_token);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.user_email);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.dqpro_v2_google_auth_redirect);
        } catch (e) {
            // already toasted the error
        }
    };

    const handleSignOut = async (event) => {
        handleClose(event);
        await (onLogout || defaultLogout)();
    };

    const itemSx = { color: 'var(--brand-primary, #FF6900)', fontSize: 14 };
    const dividerStyle = {
        margin: '4px 0',
        borderColor: isDarkMode ? '#2D2D2D' : undefined,
        backgroundColor: isDarkMode ? '#2D2D2D' : undefined,
    };

    return (
        <>
            <IconButton
                id={buttonId}
                ref={anchorRef}
                onClick={handleToggle}
                className={className}
                style={{
                    border: '2px solid var(--brand-primary, #FF6900)',
                    padding: 6,
                    backgroundColor: 'var(--brand-surface, #fff)',
                    ...style,
                }}
            >
                <AccountCircle style={{ color: 'var(--brand-primary, #FF6900)', fontSize: 28 }} />
            </IconButton>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                /*
                 * Portalled to <body> on purpose.
                 *
                 * disablePortal keeps the menu inside whatever stacking context
                 * its anchor sits in, so the zIndex below is measured against
                 * that context's children rather than the page. On the
                 * LearnCreator preview the chat panel is fixed at zIndex 1301 in
                 * the page context and painted straight over the menu.
                 */
                style={{ zIndex: 1400 }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                    >
                        <Paper style={{
                            border: isDarkMode ? '1px solid #2D2D2D' : '1px solid #E5E7EB',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            borderRadius: 10,
                            minWidth: 200,
                            backgroundColor: isDarkMode ? '#1A1A1A' : '#ffffff',
                        }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList autoFocusItem={open} id="profile-menu" onKeyDown={handleListKeyDown}>
                                    <MenuItem
                                        disabled
                                        style={{
                                            color: 'var(--brand-primary, #FF6900)',
                                            fontWeight: 700,
                                            fontSize: 14,
                                            opacity: 1,
                                        }}
                                    >
                                        {authUser?.is_guest ? 'Guest User' : (authUser?.email || 'User')}
                                    </MenuItem>

                                    {/*
                                      * MenuList takes an array of children, not a Fragment — a
                                      * Fragment makes it lose track of them for keyboard focus
                                      * and logs a warning. So these are returned as an array
                                      * rather than wrapped in <>.
                                      *
                                      * PW_PI_ST — the sheet moves "Account settings" out of the
                                      * sidebar into this menu, reworded to "Settings". It points
                                      * at the same page it always did.
                                      *
                                      * "Reset Password" is gone: sheet row 39 says "Remove (can
                                      * be found inside 'Settings')", and the Security tab does
                                      * exactly that.
                                      */}
                                    {!authUser?.is_guest && [
                                        <Divider key="pi-divider" style={dividerStyle} />,
                                        <MenuItem
                                            key="pi-settings"
                                            style={itemSx}
                                            onClick={(e) => { handleClose(e); history.push('/account/settings'); }}
                                        >
                                            Settings
                                        </MenuItem>,
                                    ]}

                                    {isMultiWorkspace && !authUser?.is_guest && (
                                        <MenuItem
                                            style={itemSx}
                                            onClick={(e) => { handleClose(e); setWsPickerOpen(true); }}
                                        >
                                            Switch Workspace
                                        </MenuItem>
                                    )}

                                    {/*
                                      * Dark mode is not offered here. Sheet row 40: "Dark Mode —
                                      * Move: inside 'Advanced Settings' under 'Settings'". The
                                      * sidebar keeps its own toggle, so this is not the only way
                                      * to reach it.
                                      */}
                                    <Divider style={dividerStyle} />
                                    {/* PW_PI_SO — the sheet rewords "Log out" to "Sign out". */}
                                    <MenuItem style={itemSx} onClick={handleSignOut}>
                                        Sign out
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            <OrgPickerDialog
                open={wsPickerOpen}
                orgs={workspaceMemberships}
                currentId={authUser?.dq_insider_id}
                currentRole={currentRole}
                loading={wsSwitching}
                onSelect={handleWorkspaceSelect}
                onCancel={() => setWsPickerOpen(false)}
            />
        </>
    );
};

export default AccountMenu;
